// filepath: apps/web/src/renderers/form/fields/StringField.tsx
//
// Single-line text input. The most common field type.
//
// SECURITY (INV-9): label and value are rendered as text nodes — no HTML.

import React from "react";
import type { FieldComponentProps } from "../FieldRegistry";
import { getUiSchemaField } from "../FieldRegistry";
import { useTranslation } from "@/i18n/useTranslation";
import { resolveLocalizedValue } from "@/i18n/localeMap";
import { useIsMobile, MIN_TOUCH_TARGET_PX } from "@/runtime/useIsMobile";

export function StringField({
  fieldId,
  value,
  onChange,
  disabled,
  uiSchema,
  fieldError,
}: FieldComponentProps): React.ReactElement {
  const { locale } = useTranslation();
  const isMobile = useIsMobile();
  const uiField = getUiSchemaField(uiSchema, fieldId);
  const labelText = resolveLocalizedValue(uiField?.label, locale, fieldId);
  const helpText = resolveLocalizedValue(uiField?.help, locale, "");
  const helpId = `field-help-${fieldId}`;
  // FR-UI-CORE-10 AC-5: compose aria-describedby so both help text and
  // the matching InlineFieldError can be announced. When neither is
  // present the attribute is omitted entirely (INV-9: no empty string).
  const errorId = fieldError?.id;
  const describedByParts = [helpText ? helpId : null, errorId].filter(
    (part): part is string => typeof part === "string",
  );
  const describedBy =
    describedByParts.length > 0 ? describedByParts.join(" ") : undefined;

  // FR-UI-NFR-3 AC-3: 48×48dp touch target; AC-5: no text truncation.
  const labelStyle: React.CSSProperties = isMobile
    ? { wordBreak: "break-word", overflowWrap: "break-word", fontWeight: 500 }
    : { fontWeight: 500 };
  const inputStyle: React.CSSProperties = isMobile
    ? {
        width: "100%",
        minHeight: `${MIN_TOUCH_TARGET_PX}px`,
        padding: "8px 12px",
        fontSize: "16px", // prevents iOS zoom on focus
        boxSizing: "border-box",
      }
    : {};

  return (
    <div data-testid={`field-${fieldId}`} className="field field-string">
      <label htmlFor={fieldId} style={labelStyle}>
        {labelText}
        {uiField?.required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={fieldId}
        type="text"
        name={fieldId}
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-required={uiField?.required ? "true" : undefined}
        aria-invalid={fieldError ? "true" : undefined}
        aria-describedby={describedBy}
        data-testid={`input-${fieldId}`}
        style={inputStyle}
      />
      {helpText ? (
        <p
          id={helpId}
          className="field-help"
          style={
            isMobile
              ? { wordBreak: "break-word", overflowWrap: "break-word" }
              : {}
          }
        >
          {helpText}
        </p>
      ) : null}
    </div>
  );
}
